import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cn } from '../lib/utils';
import {
  dateOnlyValueOf,
  formatDateOnlyDayLabel,
  formatDateOnlyMonthLabel,
  parseDateOnly,
  todayDateOnly
} from '../lib/dateOnly';
import { Button } from './ui/Button';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function EventDatePickerDialog({
  open,
  value,
  onOpenChange,
  onChange
}: {
  open: boolean;
  value: string | null;
  onOpenChange: (open: boolean) => void;
  onChange: (value: string | null) => void;
}) {
  const selectedDate = useMemo(() => (value ? parseDateOnly(value) : null), [value]);
  const todayValue = todayDateOnly();
  const initialMonth = selectedDate ?? parseDateOnly(todayValue) ?? new Date();
  const [visibleYear, setVisibleYear] = useState(initialMonth.getUTCFullYear());
  const [visibleMonth, setVisibleMonth] = useState(initialMonth.getUTCMonth());
  const [focusedValue, setFocusedValue] = useState(value ?? todayValue);
  const dayRefs = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    if (!open) {
      return;
    }
    const active = selectedDate ?? parseDateOnly(todayValue) ?? new Date();
    setVisibleYear(active.getUTCFullYear());
    setVisibleMonth(active.getUTCMonth());
    setFocusedValue(value ?? todayValue);
  }, [open, selectedDate, todayValue, value]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handle = requestAnimationFrame(() => {
      dayRefs.current.get(focusedValue)?.focus();
    });
    return () => cancelAnimationFrame(handle);
  }, [focusedValue, open, visibleMonth, visibleYear]);

  const calendarDays = useMemo(() => buildCalendarDays(visibleYear, visibleMonth), [visibleMonth, visibleYear]);
  const monthLabel = formatDateOnlyMonthLabel(visibleYear, visibleMonth);

  function moveMonth(offset: number) {
    const next = new Date(Date.UTC(visibleYear, visibleMonth + offset, 1));
    setVisibleYear(next.getUTCFullYear());
    setVisibleMonth(next.getUTCMonth());
  }

  function focusOffset(offsetDays: number) {
    const base = parseDateOnly(focusedValue) ?? new Date(Date.UTC(visibleYear, visibleMonth, 1));
    const next = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + offsetDays));
    setVisibleYear(next.getUTCFullYear());
    setVisibleMonth(next.getUTCMonth());
    setFocusedValue(dateOnlyValueOf(next));
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-black/55 backdrop-blur-[2px]" />
        <RadixDialog.Content
          aria-describedby="event-date-picker-description"
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-sm border border-ink/15 bg-ivory p-5 shadow-soft outline-none sm:p-6"
          onEscapeKeyDown={() => onOpenChange(false)}
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-xl font-semibold text-ink">Choose event date</RadixDialog.Title>
              <RadixDialog.Description id="event-date-picker-description" className="mt-1 text-sm text-ink/70">
                Pick any past or future date, or clear it if the gallery should not show one.
              </RadixDialog.Description>
            </div>
            <RadixDialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close dialog">
                <X className="h-4 w-4" />
              </Button>
            </RadixDialog.Close>
          </div>

          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <Button type="button" variant="secondary" size="icon" aria-label="Previous month" onClick={() => moveMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <p className="text-base font-semibold text-ink" aria-live="polite">{monthLabel}</p>
              <Button type="button" variant="secondary" size="icon" aria-label="Next month" onClick={() => moveMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-[0.18em] text-ink/55">
              {WEEKDAY_LABELS.map((weekday) => (
                <span key={weekday} aria-hidden="true">{weekday}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1" role="grid" aria-label={monthLabel}>
              {calendarDays.map((day) => {
                const isoValue = dateOnlyValueOf(day.date);
                const isSelected = value === isoValue;
                const isToday = isoValue === todayValue;
                const isOutsideMonth = day.date.getUTCMonth() !== visibleMonth;
                return (
                  <button
                    key={isoValue}
                    ref={(node) => {
                      if (node) {
                        dayRefs.current.set(isoValue, node);
                      } else {
                        dayRefs.current.delete(isoValue);
                      }
                    }}
                    type="button"
                    role="gridcell"
                    aria-label={formatDateOnlyDayLabel(day.date.getUTCFullYear(), day.date.getUTCMonth(), day.date.getUTCDate())}
                    aria-selected={isSelected}
                    className={cn(
                      'flex h-11 items-center justify-center rounded-sm border text-sm outline-none transition focus:ring-4 focus:ring-black/10',
                      isSelected
                        ? 'border-ink bg-ink text-white'
                        : isToday
                          ? 'border-sea/50 bg-sea/10 text-ink'
                          : 'border-transparent bg-white text-ink hover:border-ink/15 hover:bg-smoke',
                      isOutsideMonth && !isSelected ? 'text-ink/35' : null
                    )}
                    onClick={() => {
                      onChange(isoValue);
                      onOpenChange(false);
                    }}
                    onFocus={() => setFocusedValue(isoValue)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        focusOffset(-1);
                      } else if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        focusOffset(1);
                      } else if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        focusOffset(-7);
                      } else if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        focusOffset(7);
                      } else if (event.key === 'Home') {
                        event.preventDefault();
                        focusOffset(-(day.date.getUTCDay() === 0 ? 6 : day.date.getUTCDay() - 1));
                      } else if (event.key === 'End') {
                        event.preventDefault();
                        focusOffset(6 - (day.date.getUTCDay() === 0 ? 6 : day.date.getUTCDay() - 1));
                      } else if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onChange(isoValue);
                        onOpenChange(false);
                      }
                    }}
                  >
                    {day.date.getUTCDate()}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onChange(null);
                  onOpenChange(false);
                }}
              >
                Clear date
              </Button>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function buildCalendarDays(year: number, month: number): Array<{ date: Date }> {
  const monthStart = new Date(Date.UTC(year, month, 1));
  const monthStartWeekday = monthStart.getUTCDay() === 0 ? 6 : monthStart.getUTCDay() - 1;
  const gridStart = new Date(Date.UTC(year, month, 1 - monthStartWeekday));
  return Array.from({ length: 42 }, (_, index) => ({
    date: new Date(Date.UTC(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + index))
  }));
}
